import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Info, TriangleAlert } from "lucide-react";

import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionTooltipWrapper } from "components/permissions/permissions";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Subscription as SubscriptionData } from "data/models";
import { undoScheduledCancellation } from "data/mutations-common";
import { formatTimestamp } from "utils";
import { resolveIdentityId } from "utils/identity";

interface ScheduledCancellationAlertProps {
  subscription: SubscriptionData;
}

export const ScheduledCancellationAlert = ({
  subscription,
}: ScheduledCancellationAlertProps) => {
  const [open, setOpen] = useState(false);
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const active = subscription.activeSubscription!;
  const renewal = formatTimestamp(active.renewalDate * 1000);

  const undoMutation = useMutation({
    mutationFn: () => undoScheduledCancellation(),
    onSuccess: () => {
      queryClient.setQueryData([QueryKeys.subscription, identityId], {
        ...subscription,
        activeSubscription: {
          ...subscription.activeSubscription,
          cancelAtPeriodEnd: false,
        },
      });
      setOpen(false);
      enqueueSnackbar(
        "You successfully cancelled your pending subscription cancellation.",
        { variant: "success" },
      );
    },
  });

  return (
    <>
      <Alert variant="info">
        <Info />
        <AlertTitle>Scheduled plan cancellation</AlertTitle>
        <AlertDescription>
          You have a scheduled cancellation to your {active.plan.display_name}{" "}
          subscription, that will take effect on the {renewal}. Until then you
          are still able to use all of the features of{" "}
          {active.plan.product_display_name}.
        </AlertDescription>
        <AlertAction>
          <PermissionTooltipWrapper
            permissionKeys={[PermissionsKeys.subscriptionUpdate]}
          >
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                undoMutation.reset();
                setOpen(true);
              }}
            >
              Cancel scheduled cancellation
            </Button>
          </PermissionTooltipWrapper>
        </AlertAction>
      </Alert>

      <Alert variant="warning">
        <TriangleAlert />
        <AlertDescription>
          All members who are not the owner will have their access to hub
          disabled at the end of the current subscription term {renewal}.
        </AlertDescription>
      </Alert>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Keep Your Subscription Active?</DialogTitle>
            <DialogDescription>
              You previously requested to cancel your subscription. By
              confirming here, you are cancelling that cancellation request.
              This means your {active.plan.display_name} subscription will
              continue, and renew on {renewal}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={undoMutation.isPending}
              onClick={() => undoMutation.mutate()}
            >
              {undoMutation.isPending ? (
                <>
                  <Spinner />
                  Confirming...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
